import React, { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, EffectFade, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import { motion } from 'framer-motion';
import { FaPhone, FaWhatsapp, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import './HeroSection.css';

const slides = [
  {
    image: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=1200&q=80',
    badge: "Qatar's Trusted Manpower Partner",
    title: (
      <>
        If you can Imagine it,<br />
        <span className="hero-bold">We can <span className="hero-accent">build it.</span></span>
      </>
    ),
    subtitle:
      'We are a people-orientated, progressive business, driven by our values to deliver lasting change for our stakeholders and the communities we work in.',
  },
  {
    image: 'https://images.unsplash.com/photo-1464983953574-0892a716854b?auto=format&fit=crop&w=1200&q=80',
    badge: 'Skilled Labor for Construction Doha',
    title: (
      <>
        Certified Tradesmen<br />
        <span className="hero-bold">for <span className="hero-accent">Qatar Projects</span></span>
      </>
    ),
    subtitle:
      'Supplying certified electricians, plumbers, masons, and welders for all major construction sites in Qatar.',
  },
  {
    image: 'https://images.unsplash.com/photo-1503389152951-9c3d029bd29c?auto=format&fit=crop&w=1200&q=80',
    badge: '24/6 Manpower Supply Qatar',
    title: (
      <>
        Immediate Mobilization<br />
        <span className="hero-bold">Anywhere in <span className="hero-accent">Qatar</span></span>
      </>
    ),
    subtitle:
      'Get skilled construction workers on your project site within 24 hours. Reliable, safety-compliant, and experienced.',
  },
];

// Framer Motion variants for staggered animation
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.18,
      delayChildren: 0.2,
    },
  },
};
const itemVariants = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } },
};

const AUTOPLAY_DELAY = 3000;

const bgZoomVariants = {
  initial: { scale: 1 },
  animate: { scale: 1.08, transition: { duration: AUTOPLAY_DELAY / 1000, ease: 'linear' } },
};

const HeroSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const progressRef = useRef();
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  // Progress bar animation
  useEffect(() => {
    setProgress(0);
    let start;
    let frame;
    function animateBar(ts) {
      if (!start) start = ts;
      const elapsed = ts - start;
      const percent = Math.min((elapsed / AUTOPLAY_DELAY) * 100, 100);
      setProgress(percent);
      if (percent < 100) {
        frame = requestAnimationFrame(animateBar);
      }
    }
    frame = requestAnimationFrame(animateBar);
    return () => cancelAnimationFrame(frame);
  }, [activeIndex]);

  const handleContactClick = (type) => {
    switch (type) {
      case 'phone':
        window.open('tel:+97444170859', '_self');
        break;
      case 'whatsapp':
        window.open('https://wa.me/97455994434', '_blank');
        break;
      default:
        break;
    }
  };

  return (
    <section className="hero-section carousel-hero">
      <Swiper
        modules={[Autoplay, Pagination, EffectFade, Navigation]}
        effect="fade"
        loop
        autoplay={{ delay: AUTOPLAY_DELAY, disableOnInteraction: false }}
        pagination={{ clickable: true }}
        navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
        className="hero-swiper"
        onSlideChange={swiper => setActiveIndex(swiper.realIndex)}
        onInit={swiper => setActiveIndex(swiper.realIndex)}
        onBeforeInit={swiper => {
          swiper.params.navigation.prevEl = prevRef.current;
          swiper.params.navigation.nextEl = nextRef.current;
        }}
      >
       {/* Navigation Arrows */}
       <button ref={prevRef} className="hero-swiper-arrow hero-swiper-arrow-left" aria-label="Previous Slide">
         <FaChevronLeft />
       </button>
       <button ref={nextRef} className="hero-swiper-arrow hero-swiper-arrow-right" aria-label="Next Slide">
         <FaChevronRight />
       </button>
        {slides.map((slide, idx) => (
          <SwiperSlide key={idx}>
            <motion.div
              className="hero-slide-bg"
              style={{ backgroundImage: `url(${slide.image})` }}
              variants={bgZoomVariants}
              initial="initial"
              animate={activeIndex === idx ? "animate" : "initial"}
            >
              <div className="hero-slide-overlay" />
              {activeIndex === idx ? (
                <motion.div
                  className="hero-slide-content"
                  variants={containerVariants}
                  initial="hidden"
                  animate="show"
                >
                  <motion.div className="hero-badge" variants={itemVariants}>{slide.badge}</motion.div>
                  <motion.h1 className="hero-title-ref" variants={itemVariants}>{slide.title}</motion.h1>
                  <motion.p className="hero-subtitle-ref" variants={itemVariants}>{slide.subtitle}</motion.p>
                  <motion.div className="hero-actions-row" variants={itemVariants}>
                    <motion.button
                      className="btn btn-primary btn-lg hero-btn"
                      onClick={() => handleContactClick('phone')}
                      whileHover={{ scale: 1.07 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <FaPhone /> Call Now
                    </motion.button>
                    <motion.button
                      className="btn btn-whatsapp btn-lg hero-btn"
                      onClick={() => handleContactClick('whatsapp')}
                      whileHover={{ scale: 1.07 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <FaWhatsapp /> WhatsApp
                    </motion.button>
                  </motion.div>
                </motion.div>
              ) : null}
              {/* Progress Bar */}
              {activeIndex === idx && (
                <div className="hero-progress-bar-container">
                  <div
                    className="hero-progress-bar"
                    style={{ '--progress': `${progress}%` }}
                  />
                </div>
              )}
            </motion.div>
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  );
};

export default HeroSection; 